import { useLocalStorageState } from 'ahooks';
import { useMemo } from 'react';
import { remove } from 'lodash-es';

type SetterParams<T> = T | ((prev: T) => T);
type Setter<T> = (state: SetterParams<T>) => any;

export const PAY_TYPE_ITEMS: Pay.PayType[] = [
  'alipay',
  'airwallex',
  'wxpay',
  'pingpong',
  'lianlian',
];

export const supportCNYPayTypes: Pay.PayType[] = ['alipay', 'wxpay'];
const supportOtherPayTypes: Pay.PayType[] = ['pingpong', 'airwallex'];

const usePayTypeState = (availablePayTypes: Pay.PayType[], cacheKey = '@fett/default-pay/type') => {
  const [payType, setPayType] = useLocalStorageState<Pay.PayType>(cacheKey, {
    defaultValue: () => {
      return 'alipay';
    },
    serializer: (v) => v ?? 'alipay',
    deserializer: (v) => {
      return PAY_TYPE_ITEMS.find((t) => t === v) || 'alipay';
    },
  });
  return useMemo(() => {
    let finalPayType = payType || 'alipay';

    if (!availablePayTypes.includes(payType)) {
      finalPayType = availablePayTypes[0];
    }

    return [finalPayType, setPayType as Setter<Pay.PayType>] as const;
  }, [payType, setPayType, availablePayTypes]);
};

export const useAvailablePayTypes = (currency: General.Currency) => {
  return useMemo(() => {
    const types = [...(currency === 'CNY' ? supportCNYPayTypes : supportOtherPayTypes)];

    if (['PHP', 'MYR'].includes(currency)) {
      return ['lianlian' as const];
    }

    if (['MXN', 'THB', 'USD', 'VND'].includes(currency)) {
      remove(types, (type) => type === 'pingpong');
    }
    return types;
  }, [currency]);
};

export default usePayTypeState;
